import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Coffee, Phone } from 'lucide-react';
import { Button } from './ui/button';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isHeroVisible, setIsHeroVisible] = useState(true);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setScrolled(scrollY > 20);
      // Hide navbar when hero is visible on home page (first 80% of viewport)
      setIsHeroVisible(scrollY < window.innerHeight * 0.8);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Menu', path: '/menu' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  // Hide navbar on home page when hero is visible
  const shouldHideNavbar = isHomePage && isHeroVisible && !isOpen;

  return (
    <>
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ 
          y: shouldHideNavbar ? -100 : 0, 
          opacity: shouldHideNavbar ? 0 : 1 
        }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className={`fixed top-4 left-4 right-4 md:left-8 md:right-8 z-50 rounded-full px-4 md:px-6 py-3 flex items-center justify-between transition-all duration-300 ${
          scrolled || !isHomePage ? 'navbar-glass shadow-lg' : 'bg-black/30 backdrop-blur-sm'
        }`}
        data-testid="navbar"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
          <div className={`w-10 h-10 ${scrolled || !isHomePage ? 'bg-[#4A3B32]' : 'bg-white/20'} rounded-full flex items-center justify-center transition-colors`}>
            <Coffee className={`w-5 h-5 ${scrolled || !isHomePage ? 'text-[#F5F1E8]' : 'text-white'}`} />
          </div>
          <span 
            className={`font-bold text-xl ${scrolled || !isHomePage ? 'text-[#4A3B32]' : 'text-white'} transition-colors`} 
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            A1 Cafe
          </span>
        </Link>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.name.toLowerCase()}`}
              className={`font-medium transition-colors duration-300 ${
                isActive(link.path)
                  ? 'text-[#E07A5F]'
                  : scrolled || !isHomePage 
                    ? 'text-[#4A3B32] hover:text-[#E07A5F]'
                    : 'text-white hover:text-[#F2CC8F]'
              }`}
            >
              {link.name}
            </Link>
          ))}
        </div>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a href="tel:+919611475342" data-testid="call-btn-desktop">
            <Button 
              variant="outline" 
              size="sm" 
              className={`rounded-full ${
                scrolled || !isHomePage 
                  ? 'border-[#4A3B32] text-[#4A3B32] hover:bg-[#4A3B32] hover:text-white'
                  : 'border-white text-white hover:bg-white hover:text-[#4A3B32]'
              }`}
            >
              <Phone className="w-4 h-4 mr-2" />
              Call Now
            </Button>
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full ${
            scrolled || !isHomePage ? 'bg-[#4A3B32] text-white' : 'bg-white/20 text-white'
          } transition-colors`}
          data-testid="mobile-menu-btn"
          aria-label={isOpen ? 'Close menu' : 'Open menu'}
        >
          {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#F9F7F2] pt-24 px-6 md:hidden"
            data-testid="mobile-menu"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, staggerChildren: 0.1 }}
              className="flex flex-col gap-6"
            >
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                >
                  <Link
                    to={link.path}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                    className={`text-3xl font-bold block py-3 border-b border-[#E6DCC8] ${
                      isActive(link.path) ? 'text-[#E07A5F]' : 'text-[#4A3B32]'
                    }`}
                    style={{ fontFamily: 'Fraunces, serif' }}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-8 flex flex-col gap-4"
              >
                <a
                  href="https://wa.me/919611475342?text=Hi! I'd like to place an order from A1 Cafe"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-whatsapp px-6 py-4 rounded-full text-center font-bold text-lg"
                  data-testid="mobile-whatsapp-btn"
                >
                  Order on WhatsApp
                </a>
                <a
                  href="tel:+919611475342"
                  className="btn-secondary px-6 py-4 rounded-full text-center font-bold text-lg"
                  data-testid="mobile-call-btn"
                >
                  <Phone className="w-5 h-5 inline mr-2" />
                  Call to Order
                </a>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
